import type {
  FileState,
  ReportFormat,
  RunSource,
  RunStatus,
  ScheduleFrequency,
  SchedulePeriod,
  ScheduleStatus,
  Weekday,
} from './reports.types';

/** Glyph sizes, from the design: tag icons 12 px, button and inline icons 16 px, state icons 20 px. */
export const TAG_ICON_PX = 12;
export const BUTTON_ICON_PX = 16;
export const STATE_ICON_PX = 20;

/** How often a run that is still Queued or Running is read again (HS-1601). */
export const RUN_POLL_INTERVAL_MS = 3000;

/** How often the history list refreshes while any visible row is still in progress (HS-1602). */
export const LIST_POLL_INTERVAL_MS = 5000;

/** Rows per page of a run's data preview. */
export const PREVIEW_PAGE_SIZE = 25;

/** The largest page the reports endpoints accept (HS-1594, HS-891). */
export const REPORTS_MAX_PAGE_SIZE = 100;

/** How many future fires the schedule preview lists under the form (HS-891 `schedules/preview`). */
export const SCHEDULE_PREVIEW_FIRES = 3;

/** Wire percentages are 0…100 (HS-1596); `Intl` percent style wants 0…1. */
export const PERCENT_DENOMINATOR = 100;

/** A week known to start on a Monday (1 Jan 2024), for naming weekdays through `Intl`. */
export const REFERENCE_MONDAY = { year: 2024, month: 0 } as const;

export const DAYS_PER_WEEK = 7;
export const MONTHS_PER_QUARTER = 3;
export const MONTHS_PER_YEAR = 12;
export const MS_PER_DAY = 86_400_000;

/** Days in a "last 7 days" preset, counting today. */
export const LAST_DAYS_PRESET_LENGTH = 7;

/** A month day a monthly or quarterly schedule may fire on — 1…28, so every month has it. */
export const SCHEDULE_MAX_DAY_OF_MONTH = 28;

/** Default fire time of a new schedule. */
export const DEFAULT_RUN_AT = '06:00';

/** Default fire day of a new monthly schedule. */
export const DEFAULT_DAY_OF_MONTH = 1;

export const REPORT_FORMATS: readonly ReportFormat[] = ['Pdf', 'Csv'];

export const RUN_STATUSES: readonly RunStatus[] = [
  'Queued',
  'Running',
  'Completed',
  'Failed',
  'Expired',
];

/** A run in one of these may still change; the screens read it again until it settles. */
export const ACTIVE_RUN_STATUSES: readonly RunStatus[] = ['Queued', 'Running'];

export const RUN_SOURCES: readonly RunSource[] = ['Manual', 'Scheduled'];

export const FILE_STATES: readonly FileState[] = [
  'NotRequested',
  'Pending',
  'Available',
  'NoData',
  'Expired',
  'Failed',
];

export const SCHEDULE_FREQUENCIES: readonly ScheduleFrequency[] = [
  'Daily',
  'Weekly',
  'Monthly',
  'Quarterly',
];

export const SCHEDULE_PERIODS: readonly SchedulePeriod[] = [
  'PreviousDay',
  'PreviousWeek',
  'PreviousMonth',
  'PreviousQuarter',
];

export const SCHEDULE_STATUSES: readonly ScheduleStatus[] = ['Active', 'Paused', 'Ended'];

/** Monday first, the ISO order the backend's `DayOfWeek` names follow. */
export const WEEKDAYS: readonly Weekday[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

/**
 * The rolling period a schedule of each frequency covers. The form derives it from the frequency
 * (QB13); an Edit keeps a stored non-default period until the frequency changes.
 */
export const DEFAULT_PERIOD_FOR_FREQUENCY: Record<ScheduleFrequency, SchedulePeriod> = {
  Daily: 'PreviousDay',
  Weekly: 'PreviousWeek',
  Monthly: 'PreviousMonth',
  Quarterly: 'PreviousQuarter',
};

/** The period presets the run form offers beside the two dates. */
export const PERIOD_PRESETS = ['last7Days', 'lastMonth', 'lastQuarter', 'custom'] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

/**
 * Below this width of the list card (px, `Table`'s `cardsBelowWidth`) History and Schedules render
 * cards instead of the table (HS-961 responsive pass). It is History's narrowest table that does not
 * scroll: the frame's eight columns (177 + 178 + 178 + 109 + 104 + 87 + 138 + 100 = 1071 px) plus the
 * 56 px actions column, less what the chips and dates give back before they spill — 1056 px measured.
 * Schedules shares it (its nine columns fit from ~1030 px once Run time and Format may wrap), so the
 * switch between the two views never flips the layout. The card is 1114 px at 1440 (table), 954 px at
 * 1280 with the sidebar open and 698 / 442 px at 1024 / 768: cards there, because a table that
 * scrolls hides Status and the row menu behind the fold. Collapsing the sidebar at 1280 gives the
 * table back.
 */
export const REPORT_LISTS_CARDS_BELOW_PX = 1056;

/** The most columns a run's data preview draws (Audit trail, Hood utilization). */
const PREVIEW_MAX_COLUMNS = 7;
/** The narrowest preview column that keeps a chip or a two-line date on its lines (6.5 rem). */
const PREVIEW_MIN_COLUMN_PX = 104;
/** The table's 16 px outer cell padding, on both edges. */
const TABLE_EDGE_PX = 32;

/**
 * Below this width of the detail card (px) its data preview and delivery table render cards
 * (HS-961 responsive pass): seven columns at 104 px plus the edges, 760 px. The card is 954 px at
 * 1280 (table) and 698 / 442 px at 1024 / 768 with the sidebar open (cards). Delivery has two
 * columns but shares the value, so the card's two tables always present the same way.
 */
export const DETAIL_TABLES_CARDS_BELOW_PX =
  PREVIEW_MAX_COLUMNS * PREVIEW_MIN_COLUMN_PX + TABLE_EDGE_PX;
